import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActionEvent,
  BadgeComponent,
  ButtonComponent,
  CheckboxComponent,
  EmptyComponent,
  InputComponent,
  ResultsCounterComponent,
  TableComponent,
  type TableConfig,
} from 'nebula-ui-kit';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { PreservationService } from '../../../../data-access/src/lib/preservation/preservation.service';
import type {
  FilePreservationStatus,
  PreservationFileStatus,
  PreservationRepairResponse,
  PreservationStatus,
} from '../../../../domain/src/lib/preservation/preservation.model';
import { ServerContextService } from '../servers/server-context.service';
import { OaisStage, OaisStatusIndicatorComponent } from './oais-status-indicator.component';
import { PreservationStatusBadgeComponent } from './preservation-status-badge.component';
import {
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
  PageHeaderAction,
  applyVaultModuleInitialLoadErrorSignals,
  PageHeaderComponent,
  toModuleAccessErrorMessage,
  VaultModuleShellComponent,
  VaultPageAlertComponent,
} from '@nebula-shared/ui';
import { preservationStore } from '../../../../store/src/lib/preservation.store';
import {
  readVaultItemsPerPageFromPreferences,
  VAULT_TABLE_PAGE_SIZE_OPTIONS,
} from '@nebula/shared-vault/data-access';
import {
  bindVaultTablePageSizeToPreferences,
  createVaultTablePageSizeSignal,
} from '../vault-table-pagination-bind.util';

interface PreservationRow extends FilePreservationStatus {
  statusLabel: string;
  alertsCount: number;
  fileName: string;
  fileFolder: string;
  fileNameDisplay: string;
  fileFolderDisplay: string;
  lastVerifiedLabel: string;
}

interface DiagnosisIssueOption {
  issue: string;
  selected: boolean;
}

interface DiagnosticResultDialogState {
  kind: 'success' | 'error' | 'info';
  title: string;
  message: string;
}

@Component({
  selector: 'app-preservation',
  templateUrl: './preservation.component.html',
  styleUrls: ['./preservation.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    InputComponent,
    ButtonComponent,
    CheckboxComponent,
    BadgeComponent,
    TableComponent,
    ResultsCounterComponent,
    EmptyComponent,
    FieldLabelComponent,
    GridFormComponent,
    GridFormItemComponent,
    PreservationStatusBadgeComponent,
    OaisStatusIndicatorComponent,
    VaultModuleShellComponent,
    VaultPageAlertComponent,
  ],
})
export class PreservationComponent implements OnInit {
  private static readonly MODULE_LABEL = 'Preservación Digital';
  private readonly preservationService = inject(PreservationService);
  private readonly serverContext = inject(ServerContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stateStore = inject(preservationStore);

  readonly accessDenied = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorTitle = signal('');
  readonly loading = signal(false);
  readonly status = signal<PreservationStatus | null>(null);
  readonly criticalFiles = signal<PreservationRow[]>([]);
  readonly attentionFiles = signal<PreservationRow[]>([]);
  readonly optimalFiles = signal<PreservationRow[]>([]);
  readonly criticalTotal = signal(0);
  readonly attentionTotal = signal(0);
  readonly optimalTotal = signal(0);
  readonly error = signal<string | null>(null);
  readonly diagnosticError = signal<string | null>(null);
  readonly diagnosticInfo = signal<string | null>(null);
  readonly optimalEndpointUnavailable = signal(false);
  readonly optimalEndpointError = signal<string | null>(null);
  readonly diagnosticPath = signal(this.stateStore.diagnosticPath());
  readonly diagnosing = signal(false);
  readonly repairing = signal(false);
  readonly diagnoseRaw = signal<unknown | null>(null);
  readonly lastRepairResponse = signal<PreservationRepairResponse | null>(null);
  readonly issueOptions = signal<DiagnosisIssueOption[]>([]);
  readonly diagnosticResultDialog = signal<DiagnosticResultDialogState | null>(null);

  readonly tablePageSize = createVaultTablePageSizeSignal();
  readonly tablePageSizeOptions = [...VAULT_TABLE_PAGE_SIZE_OPTIONS];

  readonly headerActions = computed<PageHeaderAction[]>(() => [
    {
      id: 'reload',
      label: 'Actualizar',
      icon: 'RefreshCcw',
      variant: 'secondary',
      loading: this.loading(),
      disabled: this.loading(),
    },
  ]);

  readonly summaryCards = computed(() => {
    const summary = this.status()?.status_summary;
    if (!summary) {
      return [];
    }
    return [
      { key: 'optimal', label: 'Óptimo', value: summary.optimal, variant: 'success' as const },
      { key: 'attention', label: 'Atención', value: summary.attention, variant: 'warning' as const },
      { key: 'critical', label: 'Crítico', value: summary.critical, variant: 'error' as const },
      { key: 'unverified', label: 'Sin verificar', value: summary.unverified, variant: 'neutral' as const },
    ];
  });

  readonly oaisStage = computed<OaisStage>(() => {
    const summary = this.status()?.status_summary;
    if (!summary) return 'ingest';
    if (summary.critical > 0) return 'preservation-planning';
    if (summary.attention > 0) return 'data-management';
    if (summary.optimal > 0) return 'archival-storage';
    return 'ingest';
  });

  readonly optimalDetailHint = computed(() => {
    const summaryOptimal = this.status()?.status_summary?.optimal ?? 0;
    const detailTotal = this.optimalTotal();
    const loadedRows = this.optimalFiles().length;
    if (summaryOptimal > 0 && detailTotal > 0 && detailTotal < summaryOptimal) {
      return `Detalle de /optimal: ${detailTotal}. Resumen /status: ${summaryOptimal}.`;
    }
    if (summaryOptimal > 0 && detailTotal === 0 && loadedRows === 0) {
      return `Resumen reporta ${summaryOptimal} óptimos, pero /optimal no devolvió detalle.`;
    }
    return null;
  });

  readonly criticalTableConfig = signal<TableConfig<PreservationRow>>({
    columns: [
      { key: 'fileNameDisplay', label: 'Archivo', align: 'left' },
      { key: 'fileFolderDisplay', label: 'Ubicación', align: 'left' },
      { key: 'statusLabel', label: 'Estado', align: 'center', cellTemplate: true, width: '140px' },
      { key: 'alertsCount', label: 'Alertas', align: 'center', width: '90px' },
      { key: 'lastVerifiedLabel', label: 'Última verificación', align: 'center', width: '170px' },
    ],
    actions: [{ label: 'Usar ruta', icon: 'search', action: 'use-path' }],
    showPagination: true,
    rowClickable: true,
    defaultAction: 'use-path',
    pageSize: readVaultItemsPerPageFromPreferences(),
    pageSizeOptions: [...VAULT_TABLE_PAGE_SIZE_OPTIONS],
  });

  readonly attentionTableConfig = signal<TableConfig<PreservationRow>>({
    columns: [
      { key: 'fileNameDisplay', label: 'Archivo', align: 'left' },
      { key: 'fileFolderDisplay', label: 'Ubicación', align: 'left' },
      { key: 'statusLabel', label: 'Estado', align: 'center', cellTemplate: true, width: '140px' },
      { key: 'alertsCount', label: 'Alertas', align: 'center', width: '90px' },
      { key: 'lastVerifiedLabel', label: 'Última verificación', align: 'center', width: '170px' },
    ],
    actions: [{ label: 'Usar ruta', icon: 'search', action: 'use-path' }],
    showPagination: true,
    rowClickable: true,
    defaultAction: 'use-path',
    pageSize: readVaultItemsPerPageFromPreferences(),
    pageSizeOptions: [...VAULT_TABLE_PAGE_SIZE_OPTIONS],
  });

  readonly optimalTableConfig = signal<TableConfig<PreservationRow>>({
    columns: [
      { key: 'fileNameDisplay', label: 'Archivo', align: 'left' },
      { key: 'fileFolderDisplay', label: 'Ubicación', align: 'left' },
      { key: 'statusLabel', label: 'Estado', align: 'center', cellTemplate: true, width: '140px' },
      { key: 'alertsCount', label: 'Alertas', align: 'center', width: '90px' },
      { key: 'lastVerifiedLabel', label: 'Última verificación', align: 'center', width: '170px' },
    ],
    actions: [{ label: 'Usar ruta', icon: 'search', action: 'use-path' }],
    showPagination: true,
    rowClickable: true,
    defaultAction: 'use-path',
    pageSize: readVaultItemsPerPageFromPreferences(),
    pageSizeOptions: [...VAULT_TABLE_PAGE_SIZE_OPTIONS],
  });

  constructor() {
    bindVaultTablePageSizeToPreferences(this.tablePageSize, this.destroyRef);
  }

  ngOnInit(): void {
    this.loadDashboard();
  }

  onHeaderAction(actionId: string): void {
    if (actionId === 'reload') {
      this.loadDashboard();
    }
  }

  onTableAction(ev: ActionEvent<PreservationRow>): void {
    if (ev.action !== 'use-path') return;
    const row = ev.data;
    if (!row?.file_path) return;
    this.onDiagnosticPathChange(row.file_path);
    this.diagnosticInfo.set(`Ruta seleccionada para diagnóstico: ${row.fileName}`);
    this.diagnosticError.set(null);
  }

  closeDiagnosticResultDialog(): void {
    this.diagnosticResultDialog.set(null);
  }

  loadDashboard(): void {
    this.error.set(null);
    this.loading.set(true);
    this.optimalEndpointUnavailable.set(false);
    this.optimalEndpointError.set(null);

    forkJoin({
      status: this.preservationService.getStatus(),
      critical: this.preservationService.getCriticalFiles(100),
      attention: this.preservationService.getAttentionFiles(100),
      optimal: this.preservationService
        .getOptimalFiles(100)
        .pipe(
          catchError((err) => {
            this.optimalEndpointError.set(this.extractErrorMessage(err));
            return this.preservationService.getOptimalFiles(50);
          }),
          catchError((err) => {
            this.optimalEndpointError.set(this.extractErrorMessage(err));
            return this.preservationService.getOptimalFiles(20);
          }),
          catchError((err) => {
            this.optimalEndpointUnavailable.set(true);
            this.optimalEndpointError.set(this.extractErrorMessage(err));
            return of({ files: [], total: 0 });
          }),
        ),
    })
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ status, critical, attention, optimal }) => {
          this.status.set(status);
          this.criticalTotal.set(critical.total ?? critical.files?.length ?? 0);
          this.attentionTotal.set(attention.total ?? attention.files?.length ?? 0);
          this.optimalTotal.set(optimal.total ?? optimal.files?.length ?? 0);
          this.criticalFiles.set((critical.files ?? []).map((f) => this.toRow(f)));
          this.attentionFiles.set((attention.files ?? []).map((f) => this.toRow(f)));
          this.optimalFiles.set((optimal.files ?? []).map((f) => this.toRow(f)));
        },
        error: (err) => {
          applyVaultModuleInitialLoadErrorSignals(
            err,
            {
              moduleLabel: PreservationComponent.MODULE_LABEL,
              fallback: 'No se pudo cargar el estado de preservación.',
            },
            {
              accessDenied: this.accessDenied,
              loadError: this.loadError,
              loadErrorTitle: this.loadErrorTitle,
            },
          );
        },
      });
  }

  private extractErrorMessage(err: unknown): string {
    if (!err || typeof err !== 'object') {
      return 'Error desconocido en endpoint /optimal.';
    }
    const e = err as { status?: number; statusText?: string; error?: { detail?: string }; message?: string };
    return (
      e?.error?.detail ||
      e?.message ||
      (e?.status ? `Error ${e.status}${e.statusText ? ` (${e.statusText})` : ''} en /optimal.` : '') ||
      'No se pudo cargar /optimal.'
    );
  }

  diagnoseFile(): void {
    const filePath = this.diagnosticPath().trim();
    this.diagnosticError.set(null);
    this.diagnosticInfo.set(null);
    this.diagnoseRaw.set(null);
    this.lastRepairResponse.set(null);
    this.issueOptions.set([]);

    if (!filePath) {
      this.diagnosticError.set('Debes ingresar una ruta de archivo para diagnosticar.');
      return;
    }

    this.diagnosing.set(true);
    this.preservationService
      .diagnoseFile(filePath)
      .pipe(
        finalize(() => this.diagnosing.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          this.diagnoseRaw.set(response);
          const issues = this.extractIssues(response);
          this.issueOptions.set(issues.map((issue) => ({ issue, selected: true })));
          if (issues.length === 0) {
            const msg = 'Diagnóstico completado: no se detectaron problemas reparables para este archivo.';
            this.diagnosticInfo.set(msg);
            this.diagnosticResultDialog.set({
              kind: 'info',
              title: 'Diagnóstico completado',
              message: msg,
            });
            return;
          }
          this.diagnosticResultDialog.set({
            kind: 'success',
            title: 'Diagnóstico completado',
            message: `Se detectaron ${issues.length} problema(s) reparable(s) para este archivo.`,
          });
        },
        error: (err) => {
          const msg = toModuleAccessErrorMessage(err, {
            moduleLabel: 'Preservación Digital',
            fallback: 'No se pudo diagnosticar el archivo.',
          });
          this.diagnosticError.set(msg);
          this.diagnosticResultDialog.set({
            kind: 'error',
            title: 'Error de diagnóstico',
            message: msg,
          });
        },
      });
  }

  onDiagnosticPathChange(value: string): void {
    const next = value ?? '';
    this.diagnosticPath.set(next);
    this.stateStore.setDiagnosticPath(next);
  }

  repairSelectedIssues(): void {
    const filePath = this.diagnosticPath().trim();
    const selectedIssues = this.issueOptions()
      .filter((option) => option.selected)
      .map((option) => option.issue);

    this.diagnosticError.set(null);
    this.diagnosticInfo.set(null);
    this.lastRepairResponse.set(null);

    if (!filePath) {
      this.diagnosticError.set('Debes ingresar una ruta de archivo antes de reparar.');
      return;
    }
    if (selectedIssues.length === 0) {
      this.diagnosticError.set('Selecciona al menos un problema para reparar.');
      return;
    }

    this.repairing.set(true);
    this.preservationService
      .repairFile(filePath, selectedIssues)
      .pipe(
        finalize(() => this.repairing.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.lastRepairResponse.set(res);
          const msg = res?.message || `Reparacion ejecutada sobre ${selectedIssues.length} problema(s).`;
          this.diagnosticInfo.set(msg);
          this.diagnosticResultDialog.set({
            kind: 'success',
            title: 'Reparacion completada',
            message: msg,
          });
          this.loadDashboard();
        },
        error: (err) => {
          const msg = toModuleAccessErrorMessage(err, {
            moduleLabel: 'Preservación Digital',
            fallback: 'No se pudo ejecutar la reparación.',
          });
          this.diagnosticError.set(msg);
          this.diagnosticResultDialog.set({
            kind: 'error',
            title: 'Error de reparación',
            message: msg,
          });
        },
      });
  }

  selectAllIssues(selected: boolean): void {
    this.issueOptions.set(
      this.issueOptions().map((option) => ({ ...option, selected })),
    );
  }

  selectedIssueCount(): number {
    return this.issueOptions().filter((option) => option.selected).length;
  }

  diagnosedIssues(): string[] {
    return this.issueOptions().map((option) => option.issue);
  }

  repairedIssues(): string[] {
    return this.toStringList(this.lastRepairResponse()?.repaired_issues);
  }

  pendingIssues(): string[] {
    return this.toStringList(this.lastRepairResponse()?.pending_issues);
  }

  private extractIssues(response: unknown): string[] {
    if (!response || typeof response !== 'object') return [];
    const record = response as Record<string, unknown>;

    const candidates: unknown[] = [];
    if (Array.isArray(record['issues'])) candidates.push(record['issues']);
    if (Array.isArray(record['detected_issues'])) candidates.push(record['detected_issues']);
    if (Array.isArray(record['warnings'])) candidates.push(record['warnings']);

    for (const candidate of candidates) {
      const arr = candidate as unknown[];
      const normalized = arr
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter((value) => value.length > 0);
      if (normalized.length > 0) {
        return Array.from(new Set(normalized));
      }
    }
    return [];
  }

  private toStringList(input: unknown): string[] {
    if (!Array.isArray(input)) return [];
    return input
      .map((value) => (typeof value === 'string' ? value.trim() : ''))
      .filter((value) => value.length > 0);
  }

  private toRow(file: FilePreservationStatus): PreservationRow {
    const normalizedPath = (file.file_path || '').trim();
    const pathParts = normalizedPath.split('/').filter(Boolean);
    const fileName = pathParts[pathParts.length - 1] || normalizedPath || 'archivo';
    const fileFolder =
      pathParts.length > 1 ? '/' + pathParts.slice(0, -1).join('/') : '(raíz)';
    return {
      ...file,
      statusLabel: this.statusLabel(file.status),
      alertsCount: file.alerts?.length ?? 0,
      fileName,
      fileFolder,
      fileNameDisplay: this.truncateText(fileName, 56),
      fileFolderDisplay: this.truncateText(fileFolder, 90),
      lastVerifiedLabel: file.last_verified ? this.toDateLabel(file.last_verified) : 'Sin verificación',
    };
  }

  private toDateLabel(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toISOString().slice(0, 10);
  }

  private truncateText(value: string, max = 64): string {
    if (!value || value.length <= max) return value;
    return `${value.slice(0, max - 1)}...`;
  }

  private statusLabel(status: PreservationFileStatus): string {
    if (status === 'optimal') return 'Optimo';
    if (status === 'attention') return 'Atencion';
    if (status === 'critical') return 'Critico';
    return 'Sin verificar';
  }
}
